import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Phone, Zap, Clock, TrendingUp, Users, Shield, MessageSquare, CheckCircle, Target, ArrowRight, UserPlus, Mic, RefreshCw, FileText, BarChart3, GraduationCap, BadgeCheck, Building2, Star, PhoneCall, MessageCircle, Bot, Headphones, Award } from "lucide-react";
import PresentationDownloadButton from "../components/PresentationDownloadButton";

const CALLDRIP_LOGO = "https://www.calldrip.com/hs-fs/hubfs/calldrip-logo-white.png?width=280&height=70&name=calldrip-logo-white.png";
const CALLDRIP_LOGO_DARK = "https://www.calldrip.com/hubfs/Calldrip_February2022/Images/calldrip-logo.svg";

const slides = [
  {
    id: 1,
    type: "title",
    content: {
      headline: "Fixing the First",
      highlight: "Minutes",
      subhead: "of Every Lead",
      tagline: "Tools to Effortlessly Maximize Your Sales Team's Potential",
      stats: [
        { value: "6K+", label: "Companies" },
        { value: "15+", label: "Years" },
        { value: "30M+", label: "Conversations" },
      ]
    }
  },
  {
    id: 2,
    type: "problem",
    content: {
      label: "THE CHALLENGE",
      headline: "Speed to Lead is Everything",
      stats: [
        { value: "78%", label: "of customers buy from the first company to respond", color: "#0066cc" },
        { value: "21x", label: "more likely to connect if you call within 5 minutes", color: "#0066cc" },
        { value: "400%", label: "drop in contact rates after the first 5 minutes", color: "#cc0000" },
      ],
      insight: "Average dealer response time: 15-30 minutes. By then, the customer has already moved on."
    }
  },
  {
    id: 3,
    type: "window",
    content: {
      label: "THE WINDOW",
      headline: "When Interest is Highest",
      timeline: [
        { time: "0-2 min", label: "Peak Interest", pct: 100, color: "#00cc66" },
        { time: "2-5 min", label: "Strong Chance", pct: 75, color: "#0066cc" },
        { time: "5-10 min", label: "Declining Fast", pct: 40, color: "#ff9900" },
        { time: "20+ min", label: "Too Late", pct: 10, color: "#cc0000" },
      ],
      quote: "The first minutes are when interest is highest. After 20 minutes, you're competing, not connecting."
    }
  },
  {
    id: 4,
    type: "solution",
    content: {
      label: "THE SOLUTION",
      headline: "Rapid Lead Response",
      subtitle: "How Internet Lead Inquiry Works",
      steps: [
        { num: "1", title: "Lead Submits", desc: "Customer fills out web form" },
        { num: "2", title: "Instant Alert", desc: "Rep's phone rings, customer gets text" },
        { num: "3", title: "Call Connects", desc: "Your team is patched in" },
        { num: "4", title: "Conversation", desc: "Live engagement begins" },
      ],
      quote: "While they're still on your website, we connect you."
    }
  },
  {
    id: 5,
    type: "branded",
    content: {
      label: "BRANDED CALLING",
      headline: "Your Name. Not \"Spam Likely.\"",
      phone: {
        businessName: "ABC Motors",
        tagline: "Calling you back"
      },
      points: [
        "Bypass spam filters with verified business identity",
        "Customer sees your name within 60 seconds",
        "Higher answer rates = more conversations",
        "Professional first impression before pickup"
      ]
    }
  },
  {
    id: 6,
    type: "tools",
    content: {
      label: "PLATFORM",
      headline: "Multi-Channel Communication",
      tools: [
        { icon: "phone", title: "Rapid Lead Response", desc: "Instant call connection" },
        { icon: "message", title: "SMS Platform", desc: "Text messaging at scale" },
        { icon: "bot", title: "Virtual Assistant", desc: "AI-powered engagement" },
        { icon: "star", title: "Review Boost", desc: "Reputation management" },
      ]
    }
  },
  {
    id: 7,
    type: "integration",
    content: {
      label: "CRM INTEGRATION",
      headline: "Seamless DealerSocket Integration",
      benefits: [
        "Automatically logs and updates records",
        "Virtual Assistant appointments pushed directly into DealerSocket",
        "Eliminates duplicative data entry",
        "Drives accountability with phone insights",
        "Allows your team to focus on selling"
      ],
      features: [
        { label: "Logs New Customers", icon: "user" },
        { label: "Assigns Dealer Agent", icon: "assign" },
        { label: "Stops Response Clock", icon: "clock" },
        { label: "Tracks Every Call", icon: "track" },
        { label: "Links Recordings", icon: "record" },
        { label: "Syncs Continuously", icon: "sync" },
      ]
    }
  },
  {
    id: 8,
    type: "failsafe",
    content: {
      label: "FAILSAFE",
      headline: "No Lead Gets Ignored",
      features: [
        { title: "Auto Retry", desc: "Persistent follow-up attempts", icon: "retry" },
        { title: "Smart Routing", desc: "Next available representative", icon: "route" },
        { title: "Fallback Logic", desc: "Always a backup plan", icon: "fallback" },
      ],
      quote: "Leads don't die because someone was busy."
    }
  },
  {
    id: 9,
    type: "virtual",
    content: {
      label: "24/7 COVERAGE",
      headline: "Virtual Assistant",
      subtitle: "AI-Powered Engagement",
      features: [
        "Responds instantly, any time",
        "Captures customer intent",
        "Keeps engagement alive",
        "Schedules callbacks automatically",
        "Appointments pushed directly into DealerSocket"
      ],
      quote: "You never miss a lead again."
    }
  },
  {
    id: 10,
    type: "scorecards",
    content: {
      label: "CALL INTELLIGENCE",
      headline: "Scorecard & Coaching",
      features: [
        { title: "Customizable Scorecards", desc: "Organization or store level", icon: "scorecard" },
        { title: "Voice-to-Text", desc: "Every call transcribed", icon: "voice" },
        { title: "True Analytics", desc: "What drives appointments", icon: "analytics" },
        { title: "Complete KPI Visibility", desc: "Track every customized metric", icon: "training" },
      ],
      quote: "Stop guessing. Start knowing what works."
    }
  },
  {
    id: 11,
    type: "automation",
    content: {
      label: "POST-CALL",
      headline: "Every Call Creates Feedback",
      items: [
        { title: "Natural Follow-Up Text", desc: "Thank you message sent", icon: "message" },
        { title: "Review Request", desc: "Google review opportunity", icon: "star" },
        { title: "Feedback Loop", desc: "Internal performance data", icon: "loop" },
      ]
    }
  },
  {
    id: 12,
    type: "impact",
    content: {
      label: "THE IMPACT",
      headline: "What This Drives",
      metrics: [
        { value: "Faster", label: "Connections", icon: "zap" },
        { value: "More", label: "Conversations", icon: "chat" },
        { value: "More", label: "Appointments", icon: "calendar" },
        { value: "Higher", label: "Show Rates", icon: "check" },
      ],
      highlight: "Same leads. Better outcomes."
    }
  },
  {
    id: 13,
    type: "final",
    content: {
      headline: "Most dealers think they have a",
      strike: "lead problem",
      correction: "They have a response time problem.",
      cta: "Let's fix it together."
    }
  }
];

export default function Presentations() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goToSlide = useCallback((index) => {
    if (isAnimating || index < 0 || index >= slides.length) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsAnimating(false);
    }, 100);
  }, [isAnimating]);

  const nextSlide = useCallback(() => {
    if (currentSlide < slides.length - 1) goToSlide(currentSlide + 1);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0) goToSlide(currentSlide - 1);
  }, [currentSlide, goToSlide]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); nextSlide(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prevSlide(); }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide]);

  useEffect(() => {
    let startX = 0;
    const handleTouchStart = (e) => { startX = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) { if (diff > 0) nextSlide(); else prevSlide(); }
    };
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleTouchEnd);
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [nextSlide, prevSlide]);

  const slide = slides[currentSlide];

  const renderSlide = (slide) => {
    switch (slide.type) {
      case "title":
        return (
          <div style={styles.slideContent}>
            <h1 style={styles.titleHeadline}>
              {slide.content.headline} <span style={styles.highlight}>{slide.content.highlight}</span>
            </h1>
            <p style={styles.titleSubhead}>{slide.content.subhead}</p>
            <p style={styles.tagline}>{slide.content.tagline}</p>
            <div style={styles.statsRow}>
              {slide.content.stats.map((s, i) => (
                <div key={i} style={styles.statBox}>
                  <div style={styles.statValue}>{s.value}</div>
                  <div style={styles.statLabel}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        );

      case "problem":
        return (
          <div style={styles.slideContent}>
            <div style={styles.label}>{slide.content.label}</div>
            <h2 style={styles.headline}>{slide.content.headline}</h2>
            <div style={styles.statsGrid}>
              {slide.content.stats.map((s, i) => (
                <div key={i} style={styles.statCard}>
                  <div style={{...styles.bigStat, color: s.color}}>{s.value}</div>
                  <div style={styles.statDesc}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={styles.insight}>{slide.content.insight}</div>
          </div>
        );

      case "window":
        return (
          <div style={styles.slideContent}>
            <div style={styles.label}>{slide.content.label}</div>
            <h2 style={styles.headline}>{slide.content.headline}</h2>
            <div style={styles.timelineContainer}>
              {slide.content.timeline.map((t, i) => (
                <div key={i} style={styles.timelineItem}>
                  <div style={styles.timelineTime}>{t.time}</div>
                  <div style={{...styles.timelineBar, width: `${t.pct}%`, background: t.color}}></div>
                  <div style={styles.timelineLabel}>{t.label}</div>
                </div>
              ))}
            </div>
            <div style={styles.quoteBox}>{slide.content.quote}</div>
          </div>
        );

      case "solution":
        return (
          <div style={styles.slideContent}>
            <div style={styles.label}>{slide.content.label}</div>
            <h2 style={styles.headline}>{slide.content.headline}</h2>
            <p style={styles.subtitle}>{slide.content.subtitle}</p>
            <div style={styles.stepsRow}>
              {slide.content.steps.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={styles.stepCard}>
                    <div style={styles.stepNum}>{s.num}</div>
                    <div style={styles.stepTitle}>{s.title}</div>
                    <div style={styles.stepDesc}>{s.desc}</div>
                  </div>
                  {i < slide.content.steps.length - 1 && <ArrowRight size={24} color="#0066cc" style={{ margin: '0 10px' }} />}
                </div>
              ))}
            </div>
            <div style={styles.quoteBox}>{slide.content.quote}</div>
          </div>
        );

      case "branded":
        return (
          <div style={styles.slideContent}>
            <div style={styles.label}>{slide.content.label}</div>
            <h2 style={styles.headline}>{slide.content.headline}</h2>
            <div style={styles.brandedLayout}>
              <div style={styles.phoneMockup}>
                <div style={styles.phoneScreen}>
                  <div style={styles.phoneIcon}><Phone size={32} color="#fff" /></div>
                  <div style={styles.verifiedBadge}>
                    <BadgeCheck size={14} color="#00cc66" />
                    <span>Verified Business</span>
                  </div>
                  <div style={styles.businessName}>{slide.content.phone.businessName}</div>
                  <div style={styles.callingText}>{slide.content.phone.tagline}</div>
                  <div style={styles.phoneButtons}>
                    <div style={styles.declineBtn}><Phone size={20} color="#fff" style={{ transform: 'rotate(135deg)' }} /></div>
                    <div style={styles.acceptBtn}><Phone size={20} color="#fff" /></div>
                  </div>
                </div>
              </div>
              <div style={styles.pointsList}>
                {slide.content.points.map((p, i) => (
                  <div key={i} style={styles.pointItem}>
                    <CheckCircle size={20} color="#00cc66" />
                    <span>{p}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case "tools":
        return (
          <div style={styles.slideContent}>
            <div style={styles.label}>{slide.content.label}</div>
            <h2 style={styles.headline}>{slide.content.headline}</h2>
            <div style={styles.toolsGrid}>
              {slide.content.tools.map((t, i) => (
                <div key={i} style={styles.toolCard}>
                  <div style={styles.toolIcon}>
                    {t.icon === 'phone' && <PhoneCall size={28} color="#0066cc" />}
                    {t.icon === 'message' && <MessageCircle size={28} color="#0066cc" />}
                    {t.icon === 'bot' && <Bot size={28} color="#0066cc" />}
                    {t.icon === 'star' && <Star size={28} color="#0066cc" />}
                    {t.icon === 'headphones' && <Headphones size={28} color="#0066cc" />}
                    {t.icon === 'award' && <Award size={28} color="#0066cc" />}
                  </div>
                  <div style={styles.toolTitle}>{t.title}</div>
                  <div style={styles.toolDesc}>{t.desc}</div>
                </div>
              ))}
            </div>
          </div>
        );

      case "integration":
        return (
          <div style={styles.slideContent}>
            <div style={styles.label}>{slide.content.label}</div>
            <h2 style={styles.headline}>{slide.content.headline}</h2>
            <div style={styles.integrationLayout}>
              <div style={styles.benefitsList}>
                <h3 style={styles.sectionTitle}>Benefits</h3>
                {slide.content.benefits.map((b, i) => (
                  <div key={i} style={styles.benefitItem}>
                    <CheckCircle size={18} color="#00cc66" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
              <div style={styles.featuresGrid}>
                <h3 style={styles.sectionTitle}>What It Does</h3>
                <div style={styles.featureCards}>
                  {slide.content.features.map((f, i) => (
                    <div key={i} style={styles.featureChip}>
                      {f.icon === 'user' && <UserPlus size={16} color="#0066cc" />}
                      {f.icon === 'assign' && <Users size={16} color="#0066cc" />}
                      {f.icon === 'clock' && <Clock size={16} color="#0066cc" />}
                      {f.icon === 'track' && <Target size={16} color="#0066cc" />}
                      {f.icon === 'record' && <Mic size={16} color="#0066cc" />}
                      {f.icon === 'sync' && <RefreshCw size={16} color="#0066cc" />}
                      <span>{f.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case "failsafe":
        return (
          <div style={styles.slideContent}>
            <div style={styles.label}>{slide.content.label}</div>
            <h2 style={styles.headline}>{slide.content.headline}</h2>
            <div style={styles.failsafeGrid}>
              {slide.content.features.map((f, i) => (
                <div key={i} style={styles.failsafeCard}>
                  <div style={styles.failsafeIcon}>
                    {f.icon === 'retry' && <RefreshCw size={32} color="#0066cc" />}
                    {f.icon === 'route' && <Users size={32} color="#0066cc" />}
                    {f.icon === 'fallback' && <Shield size={32} color="#0066cc" />}
                  </div>
                  <div style={styles.failsafeTitle}>{f.title}</div>
                  <div style={styles.failsafeDesc}>{f.desc}</div>
                </div>
              ))}
            </div>
            <div style={styles.quoteBox}>{slide.content.quote}</div>
          </div>
        );

      case "virtual":
        return (
          <div style={styles.slideContent}>
            <div style={styles.label}>{slide.content.label}</div>
            <h2 style={styles.headline}>{slide.content.headline}</h2>
            <p style={styles.subtitle}>{slide.content.subtitle}</p>
            <div style={styles.virtualLayout}>
              <div style={styles.botIcon}><Bot size={64} color="#0066cc" /></div>
              <div style={styles.checkList}>
                {slide.content.features.map((f, i) => (
                  <div key={i} style={styles.checkItem}>
                    <CheckCircle size={20} color="#00cc66" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={styles.quoteBox}>{slide.content.quote}</div>
          </div>
        );

      case "scorecards":
        return (
          <div style={styles.slideContent}>
            <div style={styles.label}>{slide.content.label}</div>
            <h2 style={styles.headline}>{slide.content.headline}</h2>
            <div style={styles.scorecardsGrid}>
              {slide.content.features.map((f, i) => (
                <div key={i} style={styles.scorecardCard}>
                  <div style={styles.scorecardIcon}>
                    {f.icon === 'scorecard' && <FileText size={28} color="#0066cc" />}
                    {f.icon === 'voice' && <Mic size={28} color="#0066cc" />}
                    {f.icon === 'analytics' && <BarChart3 size={28} color="#0066cc" />}
                    {f.icon === 'training' && <GraduationCap size={28} color="#0066cc" />}
                  </div>
                  <div style={styles.scorecardTitle}>{f.title}</div>
                  <div style={styles.scorecardDesc}>{f.desc}</div>
                </div>
              ))}
            </div>
            <div style={styles.quoteBox}>{slide.content.quote}</div>
          </div>
        );

      case "automation":
        return (
          <div style={styles.slideContent}>
            <div style={styles.label}>{slide.content.label}</div>
            <h2 style={styles.headline}>{slide.content.headline}</h2>
            <div style={styles.automationGrid}>
              {slide.content.items.map((item, i) => (
                <div key={i} style={styles.automationCard}>
                  <div style={styles.automationIcon}>
                    {item.icon === 'message' && <MessageSquare size={32} color="#0066cc" />}
                    {item.icon === 'star' && <Star size={32} color="#0066cc" />}
                    {item.icon === 'loop' && <RefreshCw size={32} color="#0066cc" />}
                  </div>
                  <div style={styles.automationTitle}>{item.title}</div>
                  <div style={styles.automationDesc}>{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        );

      case "impact":
        return (
          <div style={styles.slideContent}>
            <div style={styles.label}>{slide.content.label}</div>
            <h2 style={styles.headline}>{slide.content.headline}</h2>
            <div style={styles.impactGrid}>
              {slide.content.metrics.map((m, i) => (
                <div key={i} style={styles.impactCard}>
                  <div style={styles.impactIcon}>
                    {m.icon === 'zap' && <Zap size={32} color="#0066cc" />}
                    {m.icon === 'chat' && <MessageSquare size={32} color="#0066cc" />}
                    {m.icon === 'calendar' && <Clock size={32} color="#0066cc" />}
                    {m.icon === 'check' && <CheckCircle size={32} color="#0066cc" />}
                  </div>
                  <div style={styles.impactValue}>{m.value}</div>
                  <div style={styles.impactLabel}>{m.label}</div>
                </div>
              ))}
            </div>
            <div style={styles.highlightBox}>{slide.content.highlight}</div>
          </div>
        );

      case "final":
        return (
          <div style={styles.slideContent}>
            <h2 style={styles.finalHeadline}>{slide.content.headline}</h2>
            <div style={styles.strikeText}>{slide.content.strike}</div>
            <div style={styles.correctionText}>{slide.content.correction}</div>
            <a href="https://calldrip.com/demo" target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none'}}>
              <button style={styles.ctaButton}>{slide.content.cta}</button>
            </a>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
      
      {/* Header */}
      <div style={styles.header}>
        <img src={CALLDRIP_LOGO} alt="Calldrip" style={styles.logo} />
        <div style={styles.slideCounter}>{currentSlide + 1} / {slides.length}</div>
      </div>

      {/* Slide */}
      <div style={styles.slide} key={slide.id}>
        {renderSlide(slide)}
      </div>

      {/* Download PDF button (floating top-right) */}
      <div style={{ position: "fixed", top: "20px", right: "100px", zIndex: 100 }}>
        <PresentationDownloadButton
          slides={slides}
          renderSlide={(s) => (
            <div style={{ ...styles.container, width: "1920px", height: "1080px", position: "relative" }}>
              <img src={CALLDRIP_LOGO_DARK} alt="Calldrip" style={styles.logo} />
              <div style={styles.slide}>{renderSlide(s)}</div>
            </div>
          )}
          filename="Calldrip-White-Deck"
          bgColor="#ffffff"
          className="flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-800 px-4 py-2 rounded-lg font-semibold text-sm transition-all disabled:cursor-not-allowed shadow-md"
        />
      </div>

      {/* Navigation */}
      <div style={styles.nav}>
        <button style={{...styles.navBtn, opacity: currentSlide === 0 ? 0.3 : 1}} onClick={prevSlide} disabled={currentSlide === 0}>
          <ChevronLeft size={24} />
        </button>
        <div style={styles.dots}>
          {slides.map((_, i) => (
            <div key={i} style={{...styles.dot, background: i === currentSlide ? '#0066cc' : '#ccc'}} onClick={() => goToSlide(i)} />
          ))}
        </div>
        <button style={{...styles.navBtn, opacity: currentSlide === slides.length - 1 ? 0.3 : 1}} onClick={nextSlide} disabled={currentSlide === slides.length - 1}>
          <ChevronRight size={24} />
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    width: '100vw',
    height: '100vh',
    background: 'linear-gradient(180deg, #ffffff 0%, #f5f7fa 100%)',
    fontFamily: "'Inter', -apple-system, sans-serif",
    position: 'relative',
    overflow: 'hidden',
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '70px',
    background: '#0a1628',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 40px',
    zIndex: 10,
  },
  logo: { height: '28px' },
  slideCounter: { color: 'rgba(255,255,255,0.6)', fontSize: '14px', fontWeight: 500 },
  slide: {
    width: '100%',
    height: '100%',
    paddingTop: '70px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  slideContent: {
    maxWidth: '1100px',
    width: '100%',
    padding: '40px',
    textAlign: 'center',
  },
  label: {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '2px',
    color: '#0066cc',
    marginBottom: '12px',
    textTransform: 'uppercase',
  },
  titleHeadline: {
    fontSize: 'clamp(40px, 7vw, 80px)',
    fontWeight: 800,
    color: '#0a1628',
    lineHeight: 1.1,
    marginBottom: '10px',
  },
  highlight: { color: '#0066cc' },
  titleSubhead: {
    fontSize: 'clamp(24px, 3vw, 40px)',
    fontWeight: 300,
    color: '#4a5568',
  },
  tagline: {
    fontSize: '18px',
    color: '#718096',
    marginTop: '30px',
  },
  statsRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '50px',
    marginTop: '50px',
  },
  statBox: { textAlign: 'center' },
  statValue: { fontSize: '48px', fontWeight: 800, color: '#0066cc' },
  statLabel: { fontSize: '14px', color: '#718096', textTransform: 'uppercase', letterSpacing: '1px' },
  headline: {
    fontSize: 'clamp(28px, 4vw, 48px)',
    fontWeight: 700,
    color: '#0a1628',
    marginBottom: '30px',
  },
  subtitle: { fontSize: '18px', color: '#718096', marginBottom: '30px' },
  statsGrid: {
    display: 'flex',
    justifyContent: 'center',
    gap: '30px',
    flexWrap: 'wrap',
    marginBottom: '30px',
  },
  statCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '30px 40px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    minWidth: '220px',
  },
  bigStat: { fontSize: '56px', fontWeight: 800, lineHeight: 1 },
  statDesc: { fontSize: '14px', color: '#4a5568', marginTop: '10px', maxWidth: '180px' },
  insight: {
    fontSize: '18px',
    color: '#0a1628',
    background: 'linear-gradient(90deg, rgba(0,102,204,0.1) 0%, rgba(0,102,204,0.05) 100%)',
    padding: '20px 30px',
    borderRadius: '12px',
    borderLeft: '4px solid #0066cc',
    maxWidth: '700px',
    margin: '0 auto',
    textAlign: 'left',
  },
  timelineContainer: { maxWidth: '600px', margin: '0 auto 30px' },
  timelineItem: { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' },
  timelineTime: { width: '80px', fontSize: '14px', fontWeight: 600, color: '#0a1628', textAlign: 'right' },
  timelineBar: { height: '24px', borderRadius: '4px', transition: 'all 0.3s' },
  timelineLabel: { fontSize: '14px', color: '#718096' },
  quoteBox: {
    fontSize: '16px',
    fontStyle: 'italic',
    color: '#0066cc',
    background: 'rgba(0,102,204,0.05)',
    padding: '16px 24px',
    borderRadius: '8px',
    maxWidth: '600px',
    margin: '20px auto 0',
  },
  stepsRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    marginBottom: '30px',
  },
  stepCard: {
    background: '#fff',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    width: '180px',
    minHeight: '140px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  stepNum: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: '#0066cc',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    margin: '0 auto 12px',
  },
  stepTitle: { fontSize: '16px', fontWeight: 600, color: '#0a1628', marginBottom: '4px' },
  stepDesc: { fontSize: '13px', color: '#718096', lineHeight: 1.4, textAlign: 'center' },
  brandedLayout: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '60px',
    flexWrap: 'wrap',
    marginTop: '30px',
  },
  phoneMockup: {
    width: '260px',
    background: '#1a1a2e',
    borderRadius: '32px',
    padding: '10px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  phoneScreen: {
    background: '#000',
    borderRadius: '24px',
    padding: '40px 20px',
    textAlign: 'center',
  },
  phoneIcon: {
    width: '70px',
    height: '70px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #00cc66 0%, #00aa55 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  verifiedBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    color: '#00cc66',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  businessName: { fontSize: '22px', fontWeight: 700, color: '#fff', marginBottom: '4px' },
  callingText: { fontSize: '14px', color: 'rgba(255,255,255,0.5)' },
  phoneButtons: { display: 'flex', justifyContent: 'center', gap: '40px', marginTop: '30px' },
  declineBtn: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    background: '#cc0000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  acceptBtn: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    background: '#00cc66',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pointsList: { maxWidth: '350px', textAlign: 'left' },
  pointItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '16px',
    fontSize: '16px',
    color: '#0a1628',
  },
  toolsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
    maxWidth: '600px',
    margin: '0 auto',
  },
  toolCard: {
    background: '#fff',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    textAlign: 'center',
  },
  toolIcon: { marginBottom: '12px' },
  toolTitle: { fontSize: '15px', fontWeight: 600, color: '#0a1628', marginBottom: '4px' },
  toolDesc: { fontSize: '13px', color: '#718096' },
  integrationLayout: {
    display: 'flex',
    justifyContent: 'center',
    gap: '50px',
    flexWrap: 'wrap',
    textAlign: 'left',
  },
  benefitsList: { maxWidth: '350px' },
  sectionTitle: { fontSize: '14px', fontWeight: 700, color: '#0066cc', marginBottom: '16px', textTransform: 'uppercase' },
  benefitItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginBottom: '12px',
    fontSize: '15px',
    color: '#0a1628',
  },
  featuresGrid: { maxWidth: '400px' },
  featureCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px',
  },
  featureChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(0,102,204,0.08)',
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#0a1628',
  },
  failsafeGrid: {
    display: 'flex',
    justifyContent: 'center',
    gap: '30px',
    flexWrap: 'wrap',
    marginBottom: '30px',
  },
  failsafeCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '30px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    textAlign: 'center',
    minWidth: '200px',
  },
  failsafeIcon: { marginBottom: '16px' },
  failsafeTitle: { fontSize: '18px', fontWeight: 600, color: '#0a1628', marginBottom: '6px' },
  failsafeDesc: { fontSize: '14px', color: '#718096' },
  virtualLayout: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '50px',
    marginBottom: '30px',
  },
  botIcon: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    background: 'rgba(0,102,204,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkList: { textAlign: 'left' },
  checkItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '14px',
    fontSize: '17px',
    color: '#0a1628',
  },
  scorecardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
    maxWidth: '700px',
    margin: '0 auto 30px',
  },
  scorecardCard: {
    background: '#fff',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
  },
  scorecardIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    background: 'rgba(0,102,204,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  scorecardTitle: { fontSize: '16px', fontWeight: 600, color: '#0a1628', marginBottom: '4px' },
  scorecardDesc: { fontSize: '13px', color: '#718096' },
  automationGrid: {
    display: 'flex',
    justifyContent: 'center',
    gap: '30px',
    flexWrap: 'wrap',
  },
  automationCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '30px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    textAlign: 'center',
    minWidth: '200px',
  },
  automationIcon: { marginBottom: '16px' },
  automationTitle: { fontSize: '18px', fontWeight: 600, color: '#0a1628', marginBottom: '6px' },
  automationDesc: { fontSize: '14px', color: '#718096' },
  impactGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
    maxWidth: '500px',
    margin: '0 auto 30px',
  },
  impactCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '30px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    textAlign: 'center',
    minWidth: '160px',
  },
  impactIcon: { marginBottom: '12px' },
  impactValue: { fontSize: '28px', fontWeight: 700, color: '#0066cc' },
  impactLabel: { fontSize: '14px', color: '#718096' },
  highlightBox: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#0066cc',
    background: 'rgba(0,102,204,0.08)',
    padding: '20px 30px',
    borderRadius: '12px',
    display: 'inline-block',
  },
  finalHeadline: {
    fontSize: 'clamp(20px, 3vw, 32px)',
    fontWeight: 400,
    color: '#4a5568',
    marginBottom: '10px',
  },
  strikeText: {
    fontSize: 'clamp(28px, 4vw, 48px)',
    fontWeight: 600,
    color: '#ccc',
    textDecoration: 'line-through',
  },
  correctionText: {
    fontSize: 'clamp(32px, 5vw, 56px)',
    fontWeight: 800,
    color: '#0066cc',
    marginTop: '10px',
    marginBottom: '40px',
  },
  ctaButton: {
    padding: '18px 50px',
    fontSize: '18px',
    fontWeight: 600,
    color: '#fff',
    background: 'linear-gradient(135deg, #0066cc 0%, #0052a3 100%)',
    border: 'none',
    borderRadius: '50px',
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(0,102,204,0.4)',
  },
  nav: {
    position: 'absolute',
    bottom: '30px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  navBtn: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    background: '#fff',
    border: '1px solid #e2e8f0',
    color: '#0a1628',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  dots: { display: 'flex', gap: '8px' },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};
